import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

const Products = () => {
  // Récupérer le paramètre category de l'URL
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');

  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categoryParam || 'all');
  const [selectedSubCategory, setSelectedSubCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('default');
  const [loading, setLoading] = useState(true);
  const productsPerPage = 12;

  const [products, setProducts] = useState([]);

  // ✅ Récupération des produits - Version corrigée
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await api.get('/products');
        
        // ✅ Vérification : response.data est un tableau ?
        const productsData = response.data;
        
        if (Array.isArray(productsData)) {
          // ✅ Cas normal : c'est déjà un tableau
          console.log(`✅ ${productsData.length} produits chargés (tableau direct)`);
          setProducts(productsData);
        } else if (productsData && typeof productsData === 'object') {
          // ✅ Cas où l'API renverrait un objet (par sécurité)
          console.warn('📦 Données reçues en objet, tentative d\'extraction...');
          
          // Extraire le tableau des propriétés communes
          let extractedArray = null;
          if (productsData.data && Array.isArray(productsData.data)) {
            extractedArray = productsData.data;
          } else if (productsData.products && Array.isArray(productsData.products)) {
            extractedArray = productsData.products;
          } else if (productsData.rows && Array.isArray(productsData.rows)) {
            extractedArray = productsData.rows;
          } else {
            // Si c'est un objet unique, on le met dans un tableau
            extractedArray = [productsData];
          }
          
          console.log(`✅ ${extractedArray.length} produits extraits`);
          setProducts(extractedArray);
        } else {
          // ❌ Données invalides
          console.error('❌ Les données reçues ne sont pas exploitables:', productsData);
          setProducts([]);
        }
      } catch (error) {
        console.error('❌ Erreur chargement produits:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // ✅ Vérification de sécurité : s'assurer que products est un tableau
  const safeProducts = Array.isArray(products) ? products : [];
  
  const popularProducts = safeProducts.filter(p => p.is_best_seller === true);

  const categories = [
    { id: 'all', name: 'Tous les produits' },
    { id: 'eaux', name: 'Eaux' },
    { id: 'sodas', name: 'Sodas & Boissons' },
    { id: 'laits', name: 'Laits & Nutrition' }
  ];

  const filteredProducts = safeProducts.filter(product => {
    const matchCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchSubCategory = selectedSubCategory === 'all' || product.sub_category === selectedSubCategory;
    const matchSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (product.brand && product.brand.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchCategory && matchSubCategory && matchSearch;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === 'price-asc') return (a.price || 0) - (b.price || 0);
    if (sortBy === 'price-desc') return (b.price || 0) - (a.price || 0);
    if (sortBy === 'name-asc') return (a.name || '').localeCompare(b.name || '');
    return 0;
  });

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = sortedProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(sortedProducts.length / productsPerPage);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedSubCategory('all');
    setCurrentPage(1);
  };

  const handleSubCategoryChange = (subId) => {
    setSelectedSubCategory(subId);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="container-custom py-20 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-gray-500">Chargement des produits...</p>
      </div>
    );
  }

  return (
    <div className="bg-cream">
      <div className="container-custom py-4 sm:py-6 md:py-12">
        <h1 className="text-2xl sm:text-3xl font-bold text-center mb-2 sm:mb-4">Nos produits</h1>
        <p className="text-sm sm:text-base text-center text-gray-600 mb-4 sm:mb-8 md:mb-12">Sélectionnez vos Produits</p>

        <div className="flex flex-col lg:grid lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
          {/* Sidebar Filtres - mobile en premier */}
          <div className="lg:col-span-1 order-first lg:order-none">
            <div className="bg-white rounded-xl shadow-md p-3 sm:p-4 md:p-6 sticky top-24">
              <h2 className="text-base sm:text-xl font-bold mb-3 sm:mb-4">Catégories</h2>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`block w-full text-left px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg mb-1.5 sm:mb-2 transition text-sm sm:text-base ${selectedCategory === cat.id ? 'bg-primary text-white' : 'hover:bg-gray-100'}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Produits */}
          <div className="lg:col-span-3">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
              <div className="flex gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-48 md:w-64">
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 pl-8 border border-gray-200 rounded-full text-sm focus:outline-none focus:border-secondary"
                  />
                  <svg className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-2 sm:px-3 py-2 border border-gray-200 rounded-full text-sm focus:outline-none focus:border-secondary"
                >
                  <option value="default">Trier</option>
                  <option value="price-asc">Prix croissant</option>
                  <option value="price-desc">Prix décroissant</option>
                  <option value="name-asc">Nom A-Z</option>
                </select>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-6">
              {sortedProducts.length} résultats
            </p>

            {selectedCategory === 'all' && searchTerm === '' && popularProducts.length > 0 && (
              <div className="mb-6 sm:mb-12">
                <h2 className="text-base sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-2">
                  <svg className="w-4 h-4 sm:w-6 sm:h-6 text-secondary" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.922-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                  Produits populaires
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                  {popularProducts.slice(0, 4).map(product => (
                    <ProductCard key={product.id} product={product} addToCart={addToCart} user={user} navigate={navigate} />
                  ))}
                </div>
              </div>
            )}

            {currentProducts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Aucun produit trouvé</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
                {currentProducts.map(product => (
                  <ProductCard key={product.id} product={product} addToCart={addToCart} user={user} navigate={navigate} />
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex justify-center gap-1 sm:gap-2 mt-4 sm:mt-6 flex-wrap">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 sm:px-4 sm:py-2 border rounded-lg text-sm disabled:opacity-50 hover:bg-gray-100"
                >
                  ←
                </button>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-3 py-1 sm:px-4 sm:py-2 rounded-lg text-sm ${currentPage === i + 1 ? 'bg-primary text-white' : 'border hover:bg-gray-100'}`}
                  >
                    {i + 1}
                  </button>
                ))}
                {totalPages > 5 && <span className="px-2 py-1 text-sm">...</span>}
                {totalPages > 5 && (
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    className={`px-3 py-1 sm:px-4 sm:py-2 rounded-lg text-sm ${currentPage === totalPages ? 'bg-primary text-white' : 'border hover:bg-gray-100'}`}
                  >
                    {totalPages}
                  </button>
                )}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 sm:px-4 sm:py-2 border rounded-lg text-sm disabled:opacity-50 hover:bg-gray-100"
                >
                  →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant ProductCard - Version responsive avec vérification d'authentification
const ProductCard = ({ product, addToCart, user, navigate }) => {
  const [quantity, setQuantity] = useState(1);

  const handleAddToCart = () => {
    if (!user) {
      sessionStorage.setItem('redirectAfterLogin', '/products');
      toast.error('Veuillez vous connecter pour ajouter des produits au panier');
      navigate('/login');
      return;
    }
    addToCart({ ...product, quantity });
    toast.success(`${product.name} ajouté au panier !`);
  };

  return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-md overflow-hidden hover:shadow-lg transition group">
      <Link to={`/products/${product.id}`} className="block relative overflow-hidden h-32 sm:h-40 md:h-48">
        {product.image_url ? (
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition duration-500" />
        ) : (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
            <svg className="w-8 h-8 sm:w-12 sm:h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        {product.old_price && (
          <span className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-red-500 text-white text-[9px] sm:text-xs px-1.5 py-0.5 rounded-full">Soldes</span>
        )}
        {product.is_premium && !product.old_price && (
          <span className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-secondary text-primary text-[9px] sm:text-xs px-1.5 py-0.5 rounded-full font-semibold">Premium</span>
        )}
        {product.is_best_seller && (
          <span className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-primary text-white text-[9px] sm:text-xs px-1.5 py-0.5 rounded-full font-semibold">⭐ Best</span>
        )}
      </Link>
      <div className="p-2 sm:p-3 md:p-4">
        <h3 className="font-bold text-xs sm:text-sm md:text-base line-clamp-2">{product.name}</h3>
        {product.brand && <p className="text-gray-500 text-[10px] sm:text-xs mb-0.5">{product.brand}</p>}
        <p className="text-gray-400 text-[10px] sm:text-xs mb-1">{product.format || ''}</p>
        <div className="flex items-center gap-1 sm:gap-2 mb-1.5 sm:mb-2">
          {product.old_price && (
            <span className="text-gray-400 line-through text-[10px] sm:text-xs">{product.old_price} €</span>
          )}
          <span className="text-primary font-bold text-xs sm:text-sm md:text-base">{product.price} €</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="flex items-center border rounded-lg text-xs">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="px-1.5 sm:px-2 py-0.5 sm:py-1 hover:bg-gray-100"
            >
              -
            </button>
            <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 min-w-[24px] sm:min-w-[32px] text-center">{quantity}</span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="px-1.5 sm:px-2 py-0.5 sm:py-1 hover:bg-gray-100"
            >
              +
            </button>
          </div>
          <button
            onClick={handleAddToCart}
            className="flex-1 bg-primary text-white py-1 sm:py-1.5 rounded-lg hover:bg-opacity-90 transition text-[10px] sm:text-xs font-medium"
          >
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
};

export default Products;